"use client";
"use no memo";

import { flexRender, Table as TanstackTable } from "@tanstack/react-table";
import { resolveColumnWidthStyle } from "modules/common/components/Table/columnWidth";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from "modules/common/components/Table/TableComponents";
import { TableRowSkeleton } from "modules/common/components/Table/TableRowSkeleton";
import { TableHeaderSkeleton } from "modules/common/components/Table/TableHeaderSkeleton";
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
  ViewTransition,
  type ReactNode,
} from "react";
import { cn } from "utils/utils";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { EndOfListMessage } from "modules/common/components/EndOfListCard";
import { useViewTransitionName } from "modules/common/hooks/useViewTransitionName";
import { staggeredEnterClass } from "modules/common/components/Table/viewTransitionStagger";
import { useFetchNextPageOnReach } from "modules/common/hooks/useFetchNextPageOnReach";

interface DataTableProps<TData> {
  table: TanstackTable<TData>;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  /**
   * React Query's own next-page flag. Required rather than optional: it
   * re-entrancy-guards the auto-fetch below, so a caller that omitted it
   * would fetch every page in a loop whenever the virtualizer renders the
   * whole (short) list.
   */
  isFetchingNextPage: boolean;
  /**
   * Identity of the result set — the search/sort/facet tuple that forms the
   * query key. Scroll resets to the top only when this CHANGES, which is the
   * only thing that means "the user is looking at a different list".
   *
   * Without it the component has to infer that from the rows, and it cannot:
   * "the incoming rows are not a prefix of mine" is equally true for a facet
   * change and for a row being deleted from the middle of the list, so
   * removing one card throws the user back to the top of a list they were 150
   * rows into. Omitted, that inference is what you get.
   */
  listKey?: string;
  rowCount?: number;
  containerClassName?: string;
  tableClassName?: string;
  emptyMessage?: string;
  emptyDescription?: string;
  showHover?: boolean;
  skeleton?: unknown;
  stickyHeader?: boolean;
  getRowClassName?: (row: TData, index: number) => string | undefined;
  /**
   * Tighter rows (daisyUI `table-xs`) on mobile for space-constrained views.
   * Desktop keeps the standard sizing.
   */
  compact?: boolean;
  /**
   * Animate rows that changed WITHOUT the listKey changing — a row edited or
   * removed mid-list, which on a quantity/price sort also moves it. Off by
   * default: for a table whose rows are rewritten by something other than the
   * user that is constant motion nobody asked for. On for the lists users
   * actually edit — the collection and their trade lists.
   */
  animateEdits?: boolean;
  /** Row after the last one, once every page is loaded. Mirrors the card grid's
   *  end-of-list card so the grid/table toggle reads the same. */
  showEndOfList?: boolean;
  endOfListMessage?: string;
  endOfListDescription?: string;
}

// First-render estimate only — every rendered row reports its real height back
// to the virtualizer (see `measureRow`). Cells that carry card art, set symbols
// or condition badges land nearer 52px than these numbers, and before the rows
// were measured that gap put the spacer rows, and therefore every offset below
// the visible window, out by a fifth.
const ROW_HEIGHT = 44;
const COMPACT_ROW_HEIGHT = 32;
// Matches the `md:` breakpoint the compact classes below switch at.
const MOBILE_QUERY = "(max-width: 767px)";
// How close the end of the table gets to the screen before the next page
// loads, in viewport heights. This is literally how much table is left below
// the fold when the fetch fires, so it is the budget for the request to land
// before the user scrolls into empty space — two rows was not enough and read
// as a stall.
//
// Far lower than the grid's because table rows are ~35-52px rather than
// ~455px, so a page of BROWSE_PAGE_SIZE rows is only ~1050-1560px tall. The
// real ceiling is that page height, not the viewport: the trigger compares
// against `window.innerHeight * (1 + margin)`, so the headroom is only
// (page height - viewport height), and once the lookahead crosses that line
// the sentinel is still in range when a page lands and the next fetches
// immediately — self-limiting rather than a runaway, but it pulls pages in
// before the user has scrolled. Absolute 500 and 700 were both tried and both
// did exactly that; 0.2 screens is ~180px on a laptop and ~290px at 1440px
// tall, tracking the old hand-tuned 300 while shrinking on the small
// viewports where the old value ate most of a page. Raise it only alongside a
// bigger page size.
const NEXT_PAGE_MARGIN_SCREENS = 0.2;

interface TableLoadingSkeletonProps<T> {
  rowCount?: number;
  table: TanstackTable<T>;
  className?: string;
}

export function TableLoadingSkeleton<T>({
  rowCount = 10,
  table,
  className,
}: TableLoadingSkeletonProps<T>) {
  return (
    <TableContainer
      className={cn(
        "rounded-box border-base-content/20 bg-base-100 shadow-xs relative overflow-y-auto border",
        className
      )}
    >
      <Table className="table-zebra w-full table-fixed">
        <TableHead className="bg-base-100 sticky top-0 z-30">
          <TableHeaderSkeleton table={table} />
        </TableHead>
        <TableBody>
          {Array.from({ length: rowCount }).map((_, i) => (
            <TableRowSkeleton key={i} table={table} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface EmptyStateProps {
  message: string;
  description: string;
}

function EmptyState({ message, description }: EmptyStateProps) {
  return (
    <div className="flex min-h-[100px] items-center justify-center">
      <div className="text-center">
        <div className="text-lg font-semibold">{message}</div>
        <p className="text-base-content/60">{description}</p>
      </div>
    </div>
  );
}

export function DataTable<TData>({
  table,
  isLoading,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage,
  listKey,
  rowCount = 20,
  containerClassName = "relative max-h-[55vh] overflow-y-auto",
  tableClassName,
  emptyMessage = "No items found",
  emptyDescription = "Try adjusting your filters or search criteria or adding cards",
  showHover = false,
  skeleton,
  stickyHeader = true,
  getRowClassName,
  compact = false,
  animateEdits = false,
  showEndOfList = false,
  endOfListMessage = "You've seen it all!",
  endOfListDescription = "Check back later for new additions",
}: DataTableProps<TData>) {
  const parentRef = useRef<HTMLDivElement>(null);
  // Kept in a ref rather than read during render: the visible-window check
  // below runs on every scroll-driven virtualizer render, and reading
  // clientHeight there — worse, after a querySelector for the thead — forces a
  // synchronous layout of the whole table on each of those frames. Written on
  // mount and on resize, which is all that can change either value.
  const viewport = useRef({ height: 0, header: 0 });
  // A ref callback, NOT an effect keyed on `isLoading`: the container below is
  // only mounted on renders that show real rows, so on a client-fetched table
  // (no server prefetch) `parentRef` was still null on BOTH renders where that
  // key changed — `isLoading` goes false one render before `awaitingFirstRows`
  // does, and the skeleton is returned for both. The effect then never ran
  // again, leaving the visible window at 0 for the life of the table: every
  // row read as visible, got a view-transition name, and painted its
  // document-root snapshot over the surrounding layout on each re-key. Tied to
  // the element instead, it cannot run early or be missed, and it re-runs for
  // free when the skeleton swaps the real thead in.
  const attachViewport = useCallback((el: HTMLDivElement | null) => {
    parentRef.current = el;
    if (!el) return;

    const measure = () => {
      viewport.current = {
        height: el.clientHeight,
        header: el.querySelector("thead")?.clientHeight ?? 0,
      };
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => {
      observer.disconnect();
      parentRef.current = null;
      viewport.current = { height: 0, header: 0 };
    };
  }, []);

  // React Query updates aren't Transitions, so <ViewTransition> would never
  // activate on them — but only RE-KEYED lists (sort/filter/search) should
  // animate. Infinite-scroll appends must NOT run a view transition: snapshot
  // layers paint at the document root, unclipped by the scroll container, so
  // overscan rows visibly overflow it while the animation runs. (A
  // useDeferredValue here is wrong for the same reason: its catch-up render
  // is itself a Transition, so appends animate anyway — and it starves under
  // back-to-back page loads, which made the auto-fetch guard below loop.)
  // So: hold the rendered rows in state; appends (old row list is a prefix of
  // the new one) adopt synchronously during render, re-keys adopt inside an
  // explicit startTransition, which is what activates the animations.
  //
  // Compared by row id, NOT by row.original identity. Every caller supplies
  // getRowId, so ids are stable content keys, while the row objects are not:
  // the browse lists overlay live availability onto their items, and that
  // overlay lands a moment after the page it belongs to, replacing those item
  // objects. By identity that read as a re-key on every page load — which
  // reset the scroll to the top mid-scroll.
  const rawRows = table.getRowModel().rows;
  const [tableRows, setTableRows] = useState(rawRows);

  // A full prefix check, not just the first and last id: equal length with
  // unchanged endpoints (a sort that happens to keep them, an item moving
  // between sections) is a reorder, and a reorder must animate rather than be
  // adopted urgently — otherwise the rows snap, which is exactly what the
  // transitions below exist to prevent.
  //
  // Short-circuited on identity first: the walk is O(rows) and runs on every
  // scroll-driven virtualizer render, and nothing can have appended when the
  // row model is the very array already in state.
  const isAppend =
    rawRows !== tableRows &&
    tableRows.length > 0 &&
    rawRows.length >= tableRows.length &&
    tableRows.every((row, index) => rawRows[index]?.id === row.id);

  // No staggered entrance for appended rows: the skeleton row at the end of the
  // table (below) is the loading affordance, and it appears when the fetch
  // STARTS rather than after the rows have already landed. Stacking both just
  // delays the rows the user is waiting for. The grid keeps its stagger — a
  // card dealing in reads as deliberate where a rising table row reads as lag.
  const pendingScrollReset = useRef(false);
  // Only advanced when a re-key actually commits, so a listKey change that
  // arrives while placeholderData still holds the old rows is still seen as a
  // change on the render where the new rows land.
  const lastListKey = useRef(listKey);

  if (isAppend) {
    // Render-phase adoption: commits in this same urgent render, no Transition.
    setTableRows(rawRows);
  }

  useEffect(() => {
    // Bail BEFORE scheduling: appends were already adopted during render, so
    // reaching startTransition here means a genuine re-key. Scheduling an
    // (even self-bailing) transition on every data change batches concurrent
    // scroll-driven virtualizer updates into a transition commit, animating
    // every row that leaves the window.
    if (tableRows === rawRows) {
      // Second reset, now that the new rows are the ones on screen. The reset
      // below fires while the previous rows are still mounted, and the browser's
      // scroll anchoring pulls the offset part-way back as they are replaced.
      if (pendingScrollReset.current) {
        pendingScrollReset.current = false;
        parentRef.current?.scrollTo({ top: 0 });
      }
      return;
    }
    // Reaching here means the rows are not a prefix of mine. That is NOT on its
    // own a reason to animate, and not a reason to scroll: it is equally true of
    // a facet change and of one row being edited or removed mid-list. Only a
    // changed
    // listKey means the user asked for a different list, and so belongs at the
    // top of it — placeholderData keeps the previous rows mounted, which also
    // keeps the old scroll offset unless we reset it.
    const identityChanged =
      listKey === undefined || lastListKey.current !== listKey;
    lastListKey.current = listKey;
    if (identityChanged) {
      pendingScrollReset.current = true;
      parentRef.current?.scrollTo({ top: 0 });
    }
    // No animation while a modal is open. The view transition layer paints
    // after ALL document content "including any content rendered in the top
    // layer", and an open <dialog> is captured INTO the `root` snapshot — so
    // every named row group is a sibling painted above it, and the rows
    // animate on top of the filter modal that triggered the re-key. (The
    // mobile facet modal applies filters live, so this is the normal path
    // there, not an edge case.) Nothing is lost: the table is behind the
    // modal, so there is no animation for the user to see.
    //
    // Same for rows that changed without the list changing — an in-place edit
    // rewrites one row mid-list, which is not a prefix but is also not a new
    // list. Transitioning that snapshots the rows at the document root,
    // unclipped by the scroll container, so rows scrolled out of view paint
    // over the layout above it. Only a genuine re-key gets to animate — unless
    // the caller opted in, which the collection and trade list tables do: there
    // the in-place change is the user's own edit, and on a quantity or price
    // sort it moves the row, which is worth watching happen.
    //
    // And no animation at all on a backgrounded tab: a list left open in
    // another tab still re-keys on websocket-driven refetches, and an engine
    // skips any transition started while the document is hidden — rejecting
    // `ready` with an InvalidStateError React only filters for Chrome and
    // Firefox wording, so Safari's escapes and is reported as a recoverable
    // error.
    if (
      (!identityChanged && !animateEdits) ||
      document.hidden ||
      document.querySelector("dialog[open]")
    ) {
      setTableRows(rawRows);
      return;
    }
    startTransition(() => {
      setTableRows(rawRows);
    });
  }, [rawRows, tableRows, listKey, animateEdits]);

  const rows = tableRows.length;

  const vtName = useViewTransitionName("row");
  const cols = table.getAllColumns().length;
  // Row height is a JS estimate, so the mobile-only compact sizing can't be
  // expressed with a `md:` class here.
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const rowHeight = compact && isMobile ? COMPACT_ROW_HEIGHT : ROW_HEIGHT;

  const rowVirtualizer = useVirtualizer({
    count: rows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 5,
    // Lets the virtualizer produce rows on the very first render, before the
    // scroll element ref attaches — otherwise there's a one-frame skeleton
    // state between "data arrived" and "rows visible".
    initialRect: { width: 0, height: 1000 },
  });

  // Until a real row has been measured, getTotalSize() uses ROW_HEIGHT while
  // real rows land nearer 52px (see above) — roughly a fifth short, or ~240px
  // over a page of 30, against a 300px margin. The sentinel therefore sits too
  // high on the first commit after the skeleton swap and the check would read
  // "the end is on screen" for a table that in fact ends below the fold.
  const [rowsMeasured, setRowsMeasured] = useState(false);

  // Stable identity: an inline ref callback would detach and re-attach every
  // row's measurement on every render, which is pure churn while pages stream in.
  const measureRow = useCallback(
    (el: HTMLTableRowElement | null) => {
      rowVirtualizer.measureElement(el);
      // React bails on the repeat `true`, so this is a no-op after the first row.
      if (el) setRowsMeasured(true);
    },
    [rowVirtualizer]
  );

  // Next page loads when the end of the table nears the screen — see the hook
  // for why neither the virtualizer's window nor this container's scrollTop can
  // drive it.
  const loadMoreRef = useRef<HTMLTableCellElement>(null);
  useFetchNextPageOnReach(loadMoreRef, {
    // Gated on measurement, not just availability: see `rowsMeasured`. The
    // measurement itself re-renders, so the check re-runs with real geometry.
    hasNextPage: hasNextPage && rowsMeasured,
    isFetchingNextPage,
    fetchNextPage,
    marginScreens: NEXT_PAGE_MARGIN_SCREENS,
    rows: tableRows,
  });

  // Plain returns, no surface-level <ViewTransition>: the loading skeleton
  // only shows on first load (re-keyed queries keep previous rows via
  // placeholderData), and a surface cross-fade paints unclipped snapshots
  // over the surrounding layout. Row-level transitions below are the
  // sort/filter animation.
  // `isLoading` goes false the moment the first page lands, but the rows are
  // adopted in a passive-effect transition that commits AFTER that paint — so
  // with no previous rows to keep showing we'd flash the empty state for a
  // frame. Stay on the skeleton until the rows are actually in state.
  const awaitingFirstRows = tableRows.length === 0 && rawRows.length > 0;

  if (isLoading || awaitingFirstRows) {
    return skeleton ? (
      (skeleton as ReactNode)
    ) : (
      <TableLoadingSkeleton
        className={containerClassName}
        rowCount={rowCount || 10}
        table={table}
      />
    );
  }

  const isEmpty = rows === 0;

  if (isEmpty && !isFetchingNextPage) {
    return <EmptyState message={emptyMessage} description={emptyDescription} />;
  }

  const virtualRows = rowVirtualizer.getVirtualItems();

  // View-transition snapshots paint at the document root, NOT clipped by the
  // scroll container — so only rows FULLY inside the visible window may
  // animate. Overscan and edge-straddling rows would otherwise paint ghost
  // rows over the surrounding layout on every re-key (facet/sort/search):
  // even a row half inside the box has a full-height snapshot that spills
  // past the container edge. Excluded rows swap instantly ("none").
  // Row offsets are relative to the row area, which starts below the header —
  // so the visible window in row coordinates is the container height minus the
  // header. Without that subtraction the bottom row straddling the edge counts
  // as visible and its snapshot spills below the container.
  const viewTop = rowVirtualizer.scrollOffset ?? 0;
  const viewBottom =
    viewTop + (viewport.current.height || 1000) - viewport.current.header;

  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom =
    rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0);

  return (
    <TableContainer
      ref={attachViewport}
      className={cn(
        containerClassName,
        // overscroll-y-contain: reaching the table's current bottom mid-fetch
        // otherwise chains the gesture to the page, which lurches and has to
        // be scrolled back once the rows land. Inert while the table doesn't
        // overflow — a non-scrollable container never captures the gesture.
        // Y only: the shorthand also pins the X axis, which is what the
        // trackpad back-swipe chains to.
        "rounded-box border-base-content/20 bg-base-100 shadow-xs overscroll-y-contain border"
      )}
    >
      <Table
        className={cn(
          "w-full md:table-fixed",
          compact && "table-xs md:table-md",
          tableClassName
        )}
      >
        <TableHead
          className={cn(
            "border-base-300/30 bg-base-200 border-b",
            stickyHeader && "sticky top-0 z-30"
          )}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="border-none">
              {headerGroup.headers.map((header) => (
                <TableHeader
                  key={header.id}
                  className={cn(
                    "bg-base-100 text-base-content/70 border-none font-medium",
                    compact ? "py-1 text-xs md:py-2 md:text-sm" : "text-sm",
                    header.column.columnDef.meta?.className
                  )}
                  style={resolveColumnWidthStyle(header.column.columnDef)}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHeader>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          <>
            {paddingTop > 0 && (
              <tr>
                <td
                  colSpan={cols}
                  style={{ height: paddingTop, padding: 0, border: "none" }}
                />
              </tr>
            )}
            {(() => {
              // Delay by position in the visible window, not by position among
              // the rows actually entering — the table has no per-row "is this
              // new" bookkeeping, and on the dominant case (a sort or filter
              // re-key) every visible row enters, so the two agree. A lone
              // insertion mid-window pays a delay it did not earn.
              //
              // The cap is 8 steps of 25ms, deliberately tighter than the
              // grid's 30 steps of 20ms: these delays run on view-transition
              // pseudo-elements, and the whole document is non-interactive to
              // captured elements until the transition ends, so the cascade
              // cannot be allowed to push that out. The grid's stagger runs on
              // the live element and has no such ceiling.
              let enterOrdinal = 0;
              return virtualRows.map((virtualRow) => {
                const row = tableRows[virtualRow.index];
                const isEven = virtualRow.index % 2 === 0;
                const rowVisible =
                  virtualRow.start >= viewTop && virtualRow.end <= viewBottom;
                return (
                  // Named so rows above/below an insertion or removal glide to
                  // their new position instead of snapping.
                  <ViewTransition
                    key={row.id}
                    name={vtName(row.id)}
                    enter={
                      rowVisible
                        ? staggeredEnterClass("vt-row-enter", enterOrdinal++)
                        : "none"
                    }
                    exit={rowVisible ? "vt-row-exit" : "none"}
                    default={rowVisible ? "vt-move" : "none"}
                  >
                    <TableRow
                      ref={measureRow}
                      data-index={virtualRow.index}
                      className={cn(
                        "border-base-300/20 group border-b transition-colors duration-150",
                        isEven ? "bg-base-100" : "bg-base-200/50",
                        showHover &&
                          "hover:border-base-300/40 hover:bg-base-200/20",
                        getRowClassName?.(row.original, virtualRow.index)
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            "text-base-content border-none",
                            compact ? "px-2 py-1 md:px-3 md:py-2" : "px-3 py-2",
                            cell.column.columnDef.meta?.className
                          )}
                          style={resolveColumnWidthStyle(cell.column.columnDef)}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </ViewTransition>
                );
              });
            })()}
            {paddingBottom > 0 && (
              <tr>
                <td
                  colSpan={cols}
                  style={{
                    height: paddingBottom,
                    padding: 0,
                    border: "none",
                  }}
                />
              </tr>
            )}
            {/* One skeleton row while the next page is in flight. Appears when
                the fetch STARTS, which is the moment the user needs to know
                more is coming — an entrance animation can only report it after
                the wait is already over. */}
            {isFetchingNextPage && <TableRowSkeleton table={table} />}
            {/* Never collides with the skeleton above: that needs
                isFetchingNextPage, which implies hasNextPage. */}
            {showEndOfList && !hasNextPage && (
              <tr>
                <td colSpan={cols} className="border-none px-3 py-6">
                  <EndOfListMessage
                    message={endOfListMessage}
                    description={endOfListDescription}
                  />
                </td>
              </tr>
            )}
            {/* End-of-table sentinel: always rendered (it sits after the
                virtual window, so it is never unmounted) and takes no space. */}
            <tr aria-hidden>
              <td
                ref={loadMoreRef}
                colSpan={cols}
                style={{ height: 0, padding: 0, border: "none" }}
              />
            </tr>
          </>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
